"use client";

import { useCallback, useRef, useState } from "react";
import { Card } from "@/core/components/ui/card";
import { ChatWindowHeader } from "./chat-window-header";
import { ChatWindowTypes } from "../types";
import { useIsMobile } from "@/core/hooks/useIsMobile";
import { cn } from "@/core/utils";

const DEFAULT_RIGHT = 24;
const DEFAULT_BOTTOM = 96; // bottom-24

function clampPosition(
  right: number,
  bottom: number,
  width: number,
  height: number,
) {
  const maxRight = Math.max(0, window.innerWidth - width);
  const maxBottom = Math.max(0, window.innerHeight - height);
  return {
    right: Math.max(0, Math.min(maxRight, right)),
    bottom: Math.max(0, Math.min(maxBottom, bottom)),
  };
}

export function ChatWindow({
  body,
  footer,
  onClose,
  onNewConversation,
  isHistoryOpen,
  onOpenHistory,
  onCloseHistory,
}: ChatWindowTypes) {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({
    right: DEFAULT_RIGHT,
    bottom: DEFAULT_BOTTOM,
  });
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startRight: number;
    startBottom: number;
  } | null>(null);

  const handleDragMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current || !containerRef.current) return;
    const { offsetWidth, offsetHeight } = containerRef.current;
    const deltaX = dragRef.current.startX - e.clientX;
    const deltaY = dragRef.current.startY - e.clientY;
    const newRight = dragRef.current.startRight + deltaX;
    const newBottom = dragRef.current.startBottom + deltaY;
    setPosition(clampPosition(newRight, newBottom, offsetWidth, offsetHeight));
  }, []);

  const handleDragEnd = useCallback(() => {
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.body.style.userSelect = "";
    dragRef.current = null;
  }, [handleDragMove]);

  const handleGripMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", handleDragMove);
      document.addEventListener("mouseup", handleDragEnd);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startRight: position.right,
        startBottom: position.bottom,
      };
    },
    [position, handleDragMove, handleDragEnd],
  );

  const chatWindowClassNames = cn(
    "fixed z-[999999] rounded-[20px]",
    isMobile
      ? "inset-0 w-full h-full"
      : "w-80 max-w-[500px] lg:w-[580px] h-[min(800px,calc(100dvh-100px))] max-h-[calc(100dvh-32px)]",
  );

  return (
    <div
      ref={containerRef}
      className={chatWindowClassNames}
      style={
        isMobile
          ? undefined
          : { right: position.right, bottom: position.bottom }
      }
    >
      <Card
        className={cn(
          "flex flex-col w-full h-full max-h-full bg-white border-none shadow-2xl rounded-[20px]",
          isMobile ? "rounded-none" : "",
        )}
      >
        <ChatWindowHeader
          onClose={onClose}
          onGripMouseDown={handleGripMouseDown}
          showDragHandle={!isMobile}
          onNewConversation={onNewConversation}
          isHistoryOpen={isHistoryOpen}
          onOpenHistory={onOpenHistory}
          onCloseHistory={onCloseHistory}
        />
        <div className="overflow-hidden flex-1 min-h-0">{body}</div>
        <div className="flex-shrink-0">{footer}</div>
      </Card>
    </div>
  );
}
